<template>
	<div v-if="spotlight" class="tutorial-spotlight">
		<div
			class="tutorial-spotlight__block"
			:style="blockTop"
			@click.prevent.stop
		></div>
		<div
			class="tutorial-spotlight__block"
			:style="blockLeft"
			@click.prevent.stop
		></div>
		<div
			class="tutorial-spotlight__block"
			:style="blockRight"
			@click.prevent.stop
		></div>
		<div
			class="tutorial-spotlight__block"
			:style="blockBottom"
			@click.prevent.stop
		></div>
		<div
			v-if="rect"
			class="tutorial-spotlight__hole"
			:style="holeStyle"
		></div>
		<div
			v-if="rect"
			ref="tooltipEl"
			class="tutorial-spotlight__tooltip"
			:style="tooltipStyle"
		>
			<div
				class="tutorial-spotlight__arrow"
				:class="`tutorial-spotlight__arrow--${tooltipLayout.placement}`"
				:style="arrowStyle"
			>
				<img src="@/assets/images/tutorial/arrowhead.svg" alt="" />
			</div>
			<div
				v-if="spotlight.badge"
				class="tutorial-spotlight__badge"
				:class="`tutorial-spotlight__badge--${tooltipLayout.badgeSide}`"
			>
				{{ spotlight.badge }}
			</div>
			<div class="tutorial-spotlight__text">
				<div class="tutorial-spotlight__title">
					{{ spotlight.title }}
				</div>
				<div class="tutorial-spotlight__body">
					{{ spotlight.body }}
				</div>
			</div>
			<div
				v-if="spotlight.action === 'continue'"
				class="tutorial-spotlight__actions"
			>
				<button
					type="button"
					class="tutorial-spotlight__button"
					@click="onContinue"
				>
					Tiếp tục
				</button>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useTutorial } from '@/features/tutorial/useTutorial';
import router from '@/router';

const { canAccessTutorial, activeSpotlight, phase, continueSpotlight } =
	useTutorial();

const rect = ref(null);
const tooltipEl = ref(null);
const tooltipHeight = ref(0);
let rafId = null;
let observer = null;
let scrolledForTarget = null;
let measuredTargetId = null;
let lastMenuScrollAt = 0;
const MENU_SCROLL_COOLDOWN = 400;

function isMenuTarget(targetId) {
	return typeof targetId === 'string' && targetId.startsWith('tour-menu-');
}

function isNearViewportCenter(el) {
	const r = el.getBoundingClientRect();
	const mid = (r.top + r.bottom) / 2;
	return Math.abs(mid - window.innerHeight / 2) < 72;
}

function isTargetInView(el) {
	const r = el.getBoundingClientRect();
	const margin = 32;
	return (
		r.width > 0 &&
		r.height > 0 &&
		r.top >= margin &&
		r.bottom <= window.innerHeight - margin
	);
}

function clearScrollPad() {
	document.querySelectorAll('[data-tutorial-scroll-pad]').forEach((node) => {
		node.style.paddingBottom = '';
		node.removeAttribute('data-tutorial-scroll-pad');
	});
	document
		.querySelectorAll('.tabs, .___default__side-menu')
		.forEach((node) => {
			if (node.style.paddingBottom) node.style.paddingBottom = '';
		});
}

function scrollMenuToCenter(el) {
	const parent =
		el.closest('.___default__side-menu') ||
		document.querySelector('.___default__side-menu');
	if (!parent) {
		el.scrollIntoView({
			behavior: 'smooth',
			block: 'center',
			inline: 'nearest',
		});
		return;
	}
	parent.setAttribute('data-tutorial-scroll-pad', '1');
	parent.style.paddingBottom = `${Math.round(window.innerHeight / 2)}px`;

	requestAnimationFrame(() => {
		const latest = el.getBoundingClientRect();
		if (latest.width < 2 || latest.height < 2) {
			scrolledForTarget = null;
			return;
		}
		const parentRect = parent.getBoundingClientRect();
		const nextTop =
			parent.scrollTop +
			(latest.top - parentRect.top) -
			parent.clientHeight / 2 +
			latest.height / 2;
		parent.scrollTo({
			top: Math.max(0, nextTop),
			behavior: 'smooth',
		});
	});
}

function scrollTargetIntoView(el, targetId) {
	if (!el) return;
	const r = el.getBoundingClientRect();
	if (r.width < 2 || r.height < 2) return;

	if (!isMenuTarget(targetId)) {
		clearScrollPad();
		if (scrolledForTarget === targetId) return;
		scrolledForTarget = targetId;
		if (!isTargetInView(el)) {
			el.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
				inline: 'nearest',
			});
		}
		return;
	}

	const now = Date.now();
	if (scrolledForTarget === targetId) {
		if (isNearViewportCenter(el)) return;
		if (now - lastMenuScrollAt < MENU_SCROLL_COOLDOWN) return;
	}
	scrolledForTarget = targetId;
	lastMenuScrollAt = now;
	scrollMenuToCenter(el);
}

const spotlight = computed(() =>
	canAccessTutorial.value && phase.value === 'tour'
		? activeSpotlight.value
		: null
);

const pad = 6;

const holeBox = computed(() => {
	if (!rect.value) return null;
	return {
		top: rect.value.top - pad,
		left: rect.value.left - pad,
		width: rect.value.width + pad * 2,
		height: rect.value.height + pad * 2,
	};
});

const holeStyle = computed(() => {
	if (!holeBox.value) return {};
	return {
		top: `${holeBox.value.top}px`,
		left: `${holeBox.value.left}px`,
		width: `${holeBox.value.width}px`,
		height: `${holeBox.value.height}px`,
	};
});

const fullBlock = { top: '0', left: '0', right: '0', bottom: '0' };
const hiddenBlock = { display: 'none' };

const blockTop = computed(() => {
	if (!holeBox.value) return fullBlock;
	return {
		top: '0',
		left: '0',
		right: '0',
		height: `${Math.max(0, holeBox.value.top)}px`,
	};
});

const blockLeft = computed(() => {
	if (!holeBox.value) return hiddenBlock;
	return {
		top: `${holeBox.value.top}px`,
		left: '0',
		width: `${Math.max(0, holeBox.value.left)}px`,
		height: `${holeBox.value.height}px`,
	};
});

const blockRight = computed(() => {
	if (!holeBox.value) return hiddenBlock;
	return {
		top: `${holeBox.value.top}px`,
		left: `${holeBox.value.left + holeBox.value.width}px`,
		right: '0',
		height: `${holeBox.value.height}px`,
	};
});

const blockBottom = computed(() => {
	if (!holeBox.value) return hiddenBlock;
	return {
		top: `${holeBox.value.top + holeBox.value.height}px`,
		left: '0',
		right: '0',
		bottom: '0',
	};
});

const TOOLTIP_WIDTH = 367;
const ARROW_SIZE = 12;
const ARROW_INSET = 36;
const BADGE_SPACE = 48;

const tooltipLayout = computed(() => {
	if (!rect.value || !spotlight.value) return null;
	const gap = ARROW_SIZE;
	const margin = 16;
	const width = Math.min(TOOLTIP_WIDTH, window.innerWidth - margin * 2);
	const height = tooltipHeight.value || 200;
	const vw = window.innerWidth;
	const vh = window.innerHeight;
	const r = rect.value;
	const preferred = spotlight.value.placement || 'right';

	const space = {
		right: vw - r.right - margin,
		left: r.left - margin,
		bottom: vh - r.bottom - margin,
		top: r.top - margin,
	};
	const fits = {
		right: space.right >= width + gap,
		left: space.left >= width + gap,
		bottom: space.bottom >= height + gap,
		top: space.top >= height + gap,
	};

	let placement = preferred;
	if (!fits[placement]) {
		placement =
			['right', 'left', 'bottom', 'top'].find((side) => fits[side]) ||
			Object.keys(space).sort((a, b) => space[b] - space[a])[0];
	}

	const centerX = r.left + r.width / 2;
	const centerY = r.top + r.height / 2;
	let top = centerY - height / 2;
	let left = r.right + gap;
	if (placement === 'left') {
		left = r.left - width - gap;
	} else if (placement === 'bottom') {
		top = r.bottom + gap;
		left = centerX - width / 2;
	} else if (placement === 'top') {
		top = r.top - height - gap;
		left = centerX - width / 2;
	}

	const badgeSide = placement === 'right' ? 'right' : 'left';
	const reserveLeft = spotlight.value.badge && badgeSide === 'left';
	const reserveRight = spotlight.value.badge && badgeSide === 'right';
	const checklistReserve = 360;
	left = Math.min(
		left,
		vw - width - margin - (reserveRight ? BADGE_SPACE : 0)
	);
	left = Math.max(margin + (reserveLeft ? BADGE_SPACE : 0), left);
	if (left + width > vw - checklistReserve && top > vh - 280) {
		top = Math.min(top, vh - 280 - height);
	}
	top = Math.min(top, vh - height - margin);
	top = Math.max(margin, top);

	const clamp = (value, min, max) =>
		Math.min(Math.max(value, min), Math.max(min, max));
	const arrowOffset =
		placement === 'top' || placement === 'bottom'
			? clamp(centerX - left, ARROW_INSET, width - ARROW_INSET)
			: clamp(centerY - top, ARROW_INSET, height - ARROW_INSET);

	return { placement, badgeSide, top, left, width, arrowOffset };
});

const tooltipStyle = computed(() => {
	if (!tooltipLayout.value) return { display: 'none' };
	const { top, left, width } = tooltipLayout.value;
	return {
		top: `${top}px`,
		left: `${left}px`,
		width: `${width}px`,
	};
});

const arrowStyle = computed(() => {
	if (!tooltipLayout.value) return {};
	const { placement, arrowOffset } = tooltipLayout.value;
	const offset = `${arrowOffset - ARROW_SIZE}px`;
	return placement === 'top' || placement === 'bottom'
		? { left: offset }
		: { top: offset };
});

function measure() {
	if (!spotlight.value?.targetId) {
		rect.value = null;
		measuredTargetId = null;
		scrolledForTarget = null;
		return;
	}
	const targetId = spotlight.value.targetId;
	if (measuredTargetId && measuredTargetId !== targetId) {
		rect.value = null;
		measuredTargetId = null;
	}
	const el = document.querySelector(`[data-tour-id="${targetId}"]`);
	if (!el) {
		if (measuredTargetId !== targetId) {
			rect.value = null;
		}
		return;
	}
	scrollTargetIntoView(el, targetId);
	const r = el.getBoundingClientRect();
	if (r.width < 2 || r.height < 2) {
		if (measuredTargetId !== targetId) {
			rect.value = null;
		}
		return;
	}
	measuredTargetId = targetId;
	rect.value = {
		top: r.top,
		left: r.left,
		right: r.right,
		bottom: r.bottom,
		width: r.width,
		height: r.height,
	};
	el.classList.add('tutorial-target-active');
	document.querySelectorAll('.tutorial-target-active').forEach((node) => {
		if (node !== el) node.classList.remove('tutorial-target-active');
	});
}

function tick() {
	measure();
	const h = tooltipEl.value?.offsetHeight || 0;
	if (h && h !== tooltipHeight.value) tooltipHeight.value = h;
	rafId = window.requestAnimationFrame(tick);
}

function isAllowedEventTarget(el) {
	if (!el || !el.closest) return false;
	if (el.closest('.tutorial-checklist')) return true;
	if (el.closest('.tutorial-spotlight__tooltip')) return true;
	if (el.closest('.tutorial-fab')) return true;
	if (el.closest('.dp__menu, .dp__overlay')) return true;
	const id = spotlight.value?.targetId;
	if (id && el.closest(`[data-tour-id="${id}"]`)) {
		if (spotlight.value.action === 'continue') return false;
		return true;
	}
	return false;
}

function onContinue() {
	continueSpotlight();
}

function lockOutside(e) {
	if (!spotlight.value) return;
	if (isAllowedEventTarget(e.target)) return;
	e.preventDefault();
	e.stopPropagation();
	if (e.stopImmediatePropagation) e.stopImmediatePropagation();
}

watch(
	() => spotlight.value?.targetId,
	() => {
		scrolledForTarget = null;
		measuredTargetId = null;
		lastMenuScrollAt = 0;
		rect.value = null;
		clearScrollPad();
		document
			.querySelectorAll('.tutorial-target-active')
			.forEach((node) => node.classList.remove('tutorial-target-active'));
		if (spotlight.value) {
			measure();
		}
	},
	{ immediate: true }
);

watch(
	() => router.currentRoute.value.path,
	() => {
		scrolledForTarget = null;
		lastMenuScrollAt = 0;
	}
);

onMounted(() => {
	rafId = window.requestAnimationFrame(tick);
	window.addEventListener('resize', measure);
	window.addEventListener('scroll', measure, true);
	observer = new MutationObserver(measure);
	observer.observe(document.body, { childList: true, subtree: true });
	document.addEventListener('pointerdown', lockOutside, true);
	document.addEventListener('mousedown', lockOutside, true);
	document.addEventListener('click', lockOutside, true);
	document.addEventListener('touchstart', lockOutside, {
		capture: true,
		passive: false,
	});
});

onUnmounted(() => {
	if (rafId) cancelAnimationFrame(rafId);
	clearScrollPad();
	window.removeEventListener('resize', measure);
	window.removeEventListener('scroll', measure, true);
	observer?.disconnect();
	document.removeEventListener('pointerdown', lockOutside, true);
	document.removeEventListener('mousedown', lockOutside, true);
	document.removeEventListener('click', lockOutside, true);
	document.removeEventListener('touchstart', lockOutside, true);
	document
		.querySelectorAll('.tutorial-target-active')
		.forEach((node) => node.classList.remove('tutorial-target-active'));
});
</script>

<style scoped>
.tutorial-spotlight {
	position: fixed;
	inset: 0;
	z-index: 1150;
	pointer-events: none;
}
.tutorial-spotlight__block {
	position: absolute;
	background: rgba(107, 114, 128, 0.3);
	pointer-events: auto;
	z-index: 1150;
}
.tutorial-spotlight__hole {
	position: absolute;
	border-radius: 4px;
	background: transparent;
	pointer-events: none;
	z-index: 1151;
}
.tutorial-spotlight__tooltip {
	position: absolute;
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 12px;
	border-radius: 8px;
	background: #fff;
	pointer-events: auto;
	z-index: 1160;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.24), 0 3px 8px rgba(50, 50, 71, 0.05);
}
.tutorial-spotlight__arrow {
	position: absolute;
	display: flex;
	align-items: center;
	justify-content: center;
	pointer-events: none;
}
.tutorial-spotlight__arrow img {
	display: block;
	width: 24px;
	height: 12px;
	max-width: none;
	flex-shrink: 0;
}
.tutorial-spotlight__arrow--top,
.tutorial-spotlight__arrow--bottom {
	width: 24px;
	height: 12px;
}
.tutorial-spotlight__arrow--top {
	bottom: -12px;
}
.tutorial-spotlight__arrow--bottom {
	top: -12px;
}
.tutorial-spotlight__arrow--bottom img {
	transform: scaleY(-1);
}
.tutorial-spotlight__arrow--left,
.tutorial-spotlight__arrow--right {
	width: 12px;
	height: 24px;
}
.tutorial-spotlight__arrow--left {
	right: -12px;
}
.tutorial-spotlight__arrow--left img {
	transform: rotate(-90deg);
}
.tutorial-spotlight__arrow--right {
	left: -12px;
}
.tutorial-spotlight__arrow--right img {
	transform: rotate(90deg);
}
.tutorial-spotlight__text {
	display: flex;
	flex-direction: column;
	gap: 4px;
}
.tutorial-spotlight__title {
	font-size: 18px;
	line-height: 24px;
	font-weight: 600;
	color: #48566a;
}
.tutorial-spotlight__body {
	font-size: 14px;
	line-height: 20px;
	color: #344256;
}
.tutorial-spotlight__actions {
	display: flex;
	justify-content: flex-end;
}
.tutorial-spotlight__button {
	height: 32px;
	padding: 8px 12px;
	border-radius: 4px;
	background: #f0bb4f;
	font-size: 12px;
	line-height: 16px;
	font-weight: 600;
	color: #344256;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.24), 0 3px 8px rgba(50, 50, 71, 0.05);
}
.tutorial-spotlight__badge {
	position: absolute;
	top: 0;
	width: 40px;
	height: 40px;
	border-radius: 30px;
	background: #c8172b;
	color: #fff;
	font-size: 18px;
	line-height: 24px;
	font-weight: 600;
	display: flex;
	align-items: center;
	justify-content: center;
}
.tutorial-spotlight__badge--left {
	left: -48px;
}
.tutorial-spotlight__badge--right {
	right: -48px;
}
</style>

<style>
.da-modal-frame:has(.tutorial-target-active) .da-modal-frame__wrapper,
.da-modal-frame.tutorial-target-active .da-modal-frame__wrapper {
	pointer-events: auto;
}
</style>
